import React, { memo } from 'react';
import { 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Button,
  Card,
  CardBody,
  Chip,
  Divider
} from '@heroui/react';
import { CheckCircle, XCircle, Clock, User, EnvelopeSimple } from '@phosphor-icons/react';

interface SendResult {
  contactId: string | number;
  contactName: string;
  email: string;
  status: 'success' | 'failed';
  error?: string;
}

interface SendResultsData {
  totalSent: number;
  totalFailed: number;
  totalContacts: number;
  timestamp: string;
  senderEmail: string;
  subject: string;
  results: SendResult[];
  ccContacts?: Array<{ name: string; email: string }>;
  bccContacts?: Array<{ name: string; email: string }>;
}

interface SendResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: SendResultsData | null;
}

export const SendResultsModal: React.FC<SendResultsModalProps> = memo(({ 
  isOpen, 
  onClose, 
  results 
}) => {
  if (!results) return null;

  const successRate = Math.round((results.totalSent / results.totalContacts) * 100);

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      size="2xl"
      scrollBehavior="inside"
      placement="center"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {results.totalFailed === 0 ? (
              <CheckCircle size={24} className="text-success" />
            ) : (
              <XCircle size={24} className="text-warning" />
            )}
            <h2 className="text-lg font-semibold">
              E-mail verzonden naar {results.totalContacts} contactpersoon{results.totalContacts !== 1 ? 'en' : ''}
            </h2>
          </div>
          <p className="text-sm text-default-600 font-normal">
            {results.totalSent} succesvol • {results.totalFailed} mislukt
          </p>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Statistics Cards */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-success-50 border border-success-200">
              <CardBody className="text-center py-3">
                <div className="text-2xl font-bold text-success">{results.totalSent}</div>
                <div className="text-xs text-success-600">Succesvol verzonden</div>
              </CardBody>
            </Card>

            <Card className="bg-danger-50 border border-danger-200">
              <CardBody className="text-center py-3">
                <div className="text-2xl font-bold text-danger">{results.totalFailed}</div>
                <div className="text-xs text-danger-600">Mislukt</div>
              </CardBody>
            </Card>

            <Card className="bg-primary-50 border border-primary-200">
              <CardBody className="text-center py-3">
                <div className="text-2xl font-bold text-primary">{results.totalContacts}</div>
                <div className="text-xs text-primary-600">Totaal contactpersonen</div>
              </CardBody>
            </Card>
          </div>

          {/* Send Details */}
          <Card className="bg-default-50">
            <CardBody className="py-3">
              <div className="text-sm font-medium text-default-700 mb-2">Verzendgegevens</div>
              <div className="grid grid-cols-1 gap-2 text-xs text-default-600">
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span>Verzender: {results.senderEmail}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>Tijdstip: {new Date(results.timestamp).toLocaleString('nl-BE')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={14} />
                  <span>Onderwerp: {results.subject}</span>
                </div>
                {results.ccContacts && results.ccContacts.length > 0 && (
                  <div className="flex items-start gap-2">
                    <EnvelopeSimple size={14} className="mt-0.5 shrink-0" />
                    <span>
                      CC: {results.ccContacts.map(c => c.name !== c.email ? `${c.name} <${c.email}>` : c.email).join(', ')}
                    </span>
                  </div>
                )}
                {results.bccContacts && results.bccContacts.length > 0 && (
                  <div className="flex items-start gap-2">
                    <EnvelopeSimple size={14} className="mt-0.5 shrink-0" />
                    <span>
                      BCC: {results.bccContacts.map(c => c.name !== c.email ? `${c.name} <${c.email}>` : c.email).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          <Divider />

          {/* Contact Results List */}
          <div>
            <div className="text-sm font-medium text-default-700 mb-3 flex items-center gap-2">
              <User size={16} />
              Alle contactpersonen ({results.totalContacts})
            </div>
            <div className="space-y-2 max-h-60 overflow-auto">
              {results.results.map((result, index) => (
                <div 
                  key={`${result.contactId}-${index}`}
                  className="flex items-center justify-between p-3 bg-default-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {result.status === 'success' ? (
                      <CheckCircle size={18} className="text-success shrink-0" />
                    ) : (
                      <XCircle size={18} className="text-danger shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-default-900 truncate">
                        {result.contactName}
                      </div>
                      <div className="text-xs text-default-600 truncate">
                        {result.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="shrink-0">
                    <Chip 
                      size="sm"
                      color={result.status === 'success' ? 'success' : 'danger'}
                      variant="flat"
                    >
                      {result.status === 'success' ? 'Succesvol' : 'Mislukt'}
                    </Chip>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Success Rate */}
          {results.totalContacts > 1 && (
            <Card className={`${
              successRate === 100 ? 'bg-success-50 border-success-200' : 
              successRate >= 80 ? 'bg-warning-50 border-warning-200' : 
              'bg-danger-50 border-danger-200'
            } border`}>
              <CardBody className="text-center py-3">
                <div className={`text-lg font-bold ${
                  successRate === 100 ? 'text-success' :
                  successRate >= 80 ? 'text-warning' :
                  'text-danger'
                }`}>
                  {successRate}% Succesvol
                </div>
                <div className="text-xs text-default-600">Verzendpercentage</div>
              </CardBody>
            </Card>
          )}
        </ModalBody>

        <ModalFooter className="flex-col gap-2">
          <div className="text-xs text-default-500 text-center">
            Het formulier wordt geleegd om een nieuwe e-mail samen te stellen
          </div>
          <Button 
            color="primary" 
            onPress={onClose}
            className="px-6"
          >
            Sluiten en Nieuwe E-mail
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

SendResultsModal.displayName = 'SendResultsModal';
